import type { RawParams } from "@uirouter/react";
import dayjs from "dayjs";

import type { ColumnsType, AnyObject } from "@similarweb/pro-logic-components";
import { findAndSetColumnSortOrder, shortMonthDayAndYear, TextAnchorCell } from "@similarweb/pro-logic-components";
import { Tooltip } from "@similarweb/ui-components";

import { allTrackers } from "app/services/track/track";
import { getIndexCellPropsForTableV2 } from "app/components/React/Table/cells/IndexCell";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { numberFilter } from "app/filters/numberFilter";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";

import { renderCampaignNameCell } from "../../common/utils/campaignColumnCells";
import type { ISortProps } from "../../common/utils/utils";
import { ensureProtocol } from "../../common/utils/utils";

export const getColumns = (
    totalRecords: number,
    currentPage: number,
    pageSize: number,
    sort: ISortProps,
    params: RawParams,
): ColumnsType<AnyObject> =>
    (
        [
            {
                title: "",
                align: "center",
                fixed: "left",
                key: "index",
                ...getIndexCellPropsForTableV2(currentPage, pageSize),
            },
            {
                title: translate("ad.intelligence.advertiser.campaigns.campaigns.column.title", {
                    totalRecords: numberFilter()(totalRecords),
                }),
                tooltipTitle: translate("ad.intelligence.advertiser.campaigns.campaigns.column.tooltip.title"),
                tooltipSubtitle: translate("ad.intelligence.advertiser.campaigns.campaigns.column.tooltip"),
                dataIndex: "campaignName",
                width: 220,
                fixed: "left",
                render: renderCampaignNameCell,
            },
            {
                title: translate("ad.intelligence.advertiser.campaigns.landingPageLink.column.title"),
                tooltipTitle: translate("ad.intelligence.advertiser.campaigns.landingPageLink.column.tooltip.title"),
                tooltipSubtitle: translate("ad.intelligence.advertiser.campaigns.landingPageLink.column.tooltip"),
                dataIndex: "landingPageLink",
                width: 160,
                render: (link) => (
                    <TextAnchorCell
                        target="_blank"
                        href={ensureProtocol(link)}
                        onClick={() => {
                            allTrackers.dispatchTrackEvent("External Link", "click", `Table/URL/${link}`);
                        }}
                    >
                        {link}
                    </TextAnchorCell>
                ),
            },
            {
                title: translate("ad.intelligence.advertiser.campaigns.creatives.column.title"),
                tooltipTitle: translate("ad.intelligence.advertiser.campaigns.creatives.column.tooltip.title"),
                tooltipSubtitle: translate("ad.intelligence.advertiser.campaigns.creatives.column.tooltip"),
                dataIndex: "creativesCount",
                width: 82,
                align: "right",
                render: (CreativesCount, row) => (
                    <Tooltip content={translate("ad.intelligence.advertiser.campaigns.creatives.cell.tooltip.title")}>
                        <span>
                            <TextAnchorCell
                                onClick={() =>
                                    void displayAdvertisingNavigation.ads.go({
                                        ...params,
                                        selectedCampaigns: [row.campaignId],
                                        adsSelectedSite: params.campaignsSelectedSite as string,
                                    })
                                }
                            >
                                {abbrNumberFilter()(CreativesCount)}
                            </TextAnchorCell>
                        </span>
                    </Tooltip>
                ),
            },
            {
                title: translate("ad.intelligence.advertiser.campaigns.active.days.column.title"),
                tooltipTitle: translate("ad.intelligence.advertiser.campaigns.active.days.column.tooltip.title"),
                tooltipSubtitle: translate("ad.intelligence.advertiser.campaigns.active.days.column.tooltip"),
                dataIndex: "activeDays",
                width: 96,
                align: "left",
                sorter: true,
                render: (ActiveDays) =>
                    `${numberFilter()(ActiveDays)} ${translate(
                        ActiveDays > 1
                            ? "ad.intelligence.advertiser.campaigns.active.days.column.days"
                            : "ad.intelligence.advertiser.campaigns.active.days.column.day",
                    )}`,
            },
            {
                title: translate("ad.intelligence.advertiser.campaigns.first.seen.column.title"),
                tooltipTitle: translate("ad.intelligence.advertiser.campaigns.first.seen.column.tooltip.title"),
                tooltipSubtitle: translate("ad.intelligence.advertiser.campaigns.first.seen.column.tooltip"),
                dataIndex: "firstSeen",
                width: 120,
                align: "left",
                render: (FirstSeen) => dayjs(FirstSeen).format(shortMonthDayAndYear),
            },
            {
                title: translate("ad.intelligence.advertiser.campaigns.last.seen.column.title"),
                tooltipTitle: translate("ad.intelligence.advertiser.campaigns.last.seen.column.tooltip.title"),
                tooltipSubtitle: translate("ad.intelligence.advertiser.campaigns.last.seen.column.tooltip"),
                dataIndex: "lastSeen",
                width: 120,
                align: "left",
                render: (LastSeen) => dayjs(LastSeen).format(shortMonthDayAndYear),
                sorter: true,
            },
        ] as ColumnsType<AnyObject>
    ).map((col) => findAndSetColumnSortOrder(col, sort.sort, sort.asc));
